import React, { useMemo, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { AsyncSelect } from 'chakra-react-select';

import {
  criarBuscaEmpresasModo,
  EmpresaBuscaModo,
  IEmpresaOption,
} from '../../../../../hooks/useContaCilImportar';
import { propsMenuSelect } from '../../../../../utils/selectMenu';

const PLACEHOLDER: Record<EmpresaBuscaModo, string> = {
  razao: 'Digite a razão social...',
  cnpj: 'Digite o CNPJ...',
  id: 'Digite o id (external_id)...',
};

interface Props {
  value: IEmpresaOption | null;
  onChange: (opt: IEmpresaOption | null) => void;
  label?: string; // default "Empresa"; passar "" pra esconder
  size?: 'sm' | 'md';
  isClearable?: boolean;
  isDisabled?: boolean;
  placeholderOverride?: string; // sobrescreve o placeholder por modo
  emptyMessage?: string; // mensagem quando nada digitado
}

/**
 * Busca de empresa unificada do módulo Contábil: label + toggle
 * [Razão|CNPJ|Id] + AsyncSelect (grupo-scoped via /contacil/empresas/busca).
 * O label do option vem com `external_id · razão · CNPJ` pra desambiguar
 * matriz/filial homônimas.
 */
export function EmpresaPicker({
  value,
  onChange,
  label = 'Empresa',
  size = 'sm',
  isClearable = true,
  isDisabled,
  placeholderOverride,
  emptyMessage,
}: Props) {
  const [modo, setModo] = useState<EmpresaBuscaModo>('razao');
  const carregar = useMemo(() => criarBuscaEmpresasModo(modo), [modo]);

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={1} gap={2}>
        {label ? (
          <Text fontSize="10px" fontWeight="600" color="#64748B" textTransform="uppercase">
            {label}
          </Text>
        ) : (
          <Box />
        )}
        <Flex bg="rgba(15,23,42,0.06)" borderRadius="md" p="2px" gap="2px">
          {(
            [
              ['razao', 'Razão'],
              ['cnpj', 'CNPJ'],
              ['id', 'Id'],
            ] as const
          ).map(([m, lbl]) => (
            <Box
              key={m}
              as="button"
              type="button"
              px={2}
              py="1px"
              borderRadius="sm"
              fontSize="9px"
              fontWeight="700"
              textTransform="uppercase"
              color={modo === m ? '#0F172A' : '#94A3B8'}
              bg={modo === m ? 'white' : 'transparent'}
              boxShadow={modo === m ? 'sm' : 'none'}
              onClick={() => setModo(m)}
            >
              {lbl}
            </Box>
          ))}
        </Flex>
      </Flex>
      <AsyncSelect<IEmpresaOption>
        size={size}
        placeholder={placeholderOverride ?? PLACEHOLDER[modo]}
        loadOptions={carregar}
        value={value}
        onChange={opt => onChange(opt as IEmpresaOption | null)}
        loadingMessage={() => 'Buscando...'}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? 'Nenhuma empresa encontrada' : (emptyMessage ?? 'Digite para buscar')
        }
        isClearable={isClearable}
        isDisabled={isDisabled}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
        {...propsMenuSelect}
        chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
      />
    </Box>
  );
}
